import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore, waitForAuthHydration } from '@/store'
import { authApi, ApiErrorResponse } from '@/services/api'
import type { VerifySubstitutionTokenResponse } from '@/services/api/auth'
import { applyMagicSession, toInternalPath } from '@/utils/authRedirect'

const FALLBACK_ADMIN = '/admin/substitutions'

export function AdminVerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const attemptedRef = useRef(false)

  const [status, setStatus] = useState<'loading' | 'expired' | 'invalid' | 'success'>('loading')
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const next = searchParams.get('next')

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('invalid')
      setErrorMessage('Kein Token gefunden.')
      return
    }
    if (attemptedRef.current) return
    attemptedRef.current = true

    async function handleVerify() {
      if (!token) return
      try {
        await waitForAuthHydration()
        const response: VerifySubstitutionTokenResponse = await authApi.verifyAdminToken(token)
        const nextPath = searchParams.get('next')
        const path = toInternalPath(nextPath || response.redirect_url, FALLBACK_ADMIN)

        if (response.valid && (response.redirect_url || nextPath)) {
          const ok = await applyMagicSession(response)
          if (!ok) {
            setStatus('invalid')
            setErrorMessage('Anmeldung fehlgeschlagen.')
            return
          }
          setStatus('success')
          setRedirectUrl(path)
          setTimeout(() => navigate(path), 1000)
          return
        }

        if (!response.valid && response.redirect_url && useAuthStore.getState().isAuthenticated) {
          setStatus('success')
          setRedirectUrl(path)
          setTimeout(() => navigate(path), 1000)
          return
        }

        setStatus(response.expired ? 'expired' : 'invalid')
        setErrorMessage(response.error || (response.expired ? 'Link abgelaufen.' : 'Link ungültig.'))
      } catch (e: unknown) {
        setStatus('invalid')
        if (e instanceof ApiErrorResponse) {
          setErrorMessage(e.message || 'Verifizierung fehlgeschlagen.')
        } else {
          setErrorMessage('Verifizierung fehlgeschlagen.')
        }
      }
    }
    handleVerify()
  }, [searchParams, navigate])

  const fallbackTarget = toInternalPath(
    redirectUrl || (next ? decodeURIComponent(next) : FALLBACK_ADMIN),
    FALLBACK_ADMIN,
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && <p className="text-slate-600">Anfrage wird geöffnet …</p>}
        {status === 'success' && (
          <p className="text-emerald-700 font-medium">Weiterleitung zur Anfrage …</p>
        )}
        {status === 'expired' && (
          <>
            <p className="text-amber-700 font-medium mb-2">Der Link ist abgelaufen oder wurde bereits verwendet.</p>
            <p className="text-sm text-slate-500 mb-4">Bitte melden Sie sich normal an.</p>
            <Link to="/login" className="inline-block px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Zum Login
            </Link>
          </>
        )}
        {status === 'invalid' && (
          <>
            <p className="text-red-700 font-medium mb-2">{errorMessage || 'Link ungültig.'}</p>
            <Link to="/login" className="inline-block px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Zum Login
            </Link>
          </>
        )}
        {(isAuthenticated || redirectUrl) && status !== 'loading' && (
          <button
            onClick={() => navigate(fallbackTarget)}
            className="block mx-auto mt-4 text-sm text-blue-600 hover:underline"
          >
            Direkt zur Anfrage
          </button>
        )}
      </div>
    </div>
  )
}
